import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { voiceSearch } from '../services/apiService';
import { useNavigate } from 'react-router-dom'; 
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import './VoiceRecorder.css';

const VoiceRecorder = ({ isOpen, onClose, onSearchResults, onRagAnswer }) => {
  const { t } = useLanguage();
  const { makeAuthenticatedRequest } = useAuth();
  const navigate = useNavigate();
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [routingHint, setRoutingHint] = useState(null); // 'rag' | 'search' | null
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  /**
   * Schnelle clientseitige Vorschau: Sieht das Transkript nach einer Frage aus?
   * Spiegelt die Kernlogik von TranscribeSearchController::classifyTranscript().
   * Dient nur als sofortiges UI-Feedback — die echte Entscheidung trifft das Backend.
   */
  const looksLikeQuestionLocal = (text) => {
    const q = text.toLowerCase().trim();
    if (q.split(' ').length <= 2) return false;
    const cmds = ['dashboard', 'profil', 'profile', 'startseite', 'home', 'erstelle', 'create', 'upload'];
    if (cmds.some(c => q.startsWith(c))) return false;
    if (q.endsWith('?')) return true;
    const starters = [
      'was ', 'wie ', 'wann ', 'warum ', 'wer ', 'welche', 'wo ', 'wozu ',
      'erkläre', 'beschreibe', 'fasse', 'nenn mir', 'liste', 'erzähl', 'sag mir',
      'what ', 'how ', 'when ', 'why ', 'who ', 'which ', 'where ',
      'explain', 'describe', 'summarize', 'show me', 'can you', 'tell me',
      'give me', 'help me',
    ];
    if (starters.some(s => q.startsWith(s))) return true;
    if (/^(ist|sind|hat|haben|gibt es|kann|können|soll|darf|wurde|wird)\b/i.test(q)) return true;
    if (q.split(' ').length > 5) return true;
    return false;
  };

  const VOICE_COMMANDS = [
    { 
      triggers: ['erstelle quiz', 'neues quiz', 'quiz anlegen', 'quiz erstellen', 'create quiz', 'new quiz'], 
      route: '/create-quiz' 
    },
    { 
      triggers: ['erstelle kurs', 'neuer kurs', 'kurs anlegen', 'kurs erstellen', 'create course', 'new course'], 
      route: '/create-course' 
    },
    { 
      triggers: ['erstelle video', 'neues video', 'video erstellen', 'video hochladen', 'create video', 'upload video', 'new video'], 
      route: '/create-element' 
    },
    { 
      triggers: ['profil', 'profil anzeigen', 'profile', 'show profile'], 
      route: '/profile' 
    },
    { 
      triggers: ['startseite', 'dashboard', 'home', 'go home'], 
      route: '/dashboard' 
    }
  ];

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setTranscript('');
      setRecordingTime(0);
      setRoutingHint(null);
      try {
        document.body.classList.add('voice-recorder-scroll-lock');
      } catch (_) {}
    } else {
      try {
        document.body.classList.remove('voice-recorder-scroll-lock');
      } catch (_) {}
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      try {
        document.body.classList.remove('voice-recorder-scroll-lock');
      } catch (_) {}
    };
  }, []);

  const startRecording = async () => {
    try {
      setError(null);
      audioChunksRef.current = [];
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      // Browser-Support prüfen: WebM oder MP4 bevorzugen
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/m4a',
        'audio/ogg',
        'audio/wav'
      ];
      
      let selectedMimeType = null;
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }
      
      if (!selectedMimeType) {
        // Fallback: Leer lassen, Browser wählt Standard
        selectedMimeType = '';
      }
      
      console.log('Starting recording with MIME type:', selectedMimeType || 'default');
      
      const options = selectedMimeType ? { mimeType: selectedMimeType } : {};
      mediaRecorderRef.current = new MediaRecorder(stream, options);

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const finalMimeType = mediaRecorderRef.current.mimeType || selectedMimeType || 'audio/webm';
        console.log('Recorder stopped. Raw MIME type:', finalMimeType);

        // MIME-Type Bereinigung (für Backend-Kompatibilität)
        let cleanMimeType = finalMimeType;
        if (cleanMimeType.startsWith('video/')) {
            cleanMimeType = cleanMimeType.replace('video/', 'audio/');
        }
        // Codecs entfernen (z.B. ";codecs=opus")
        cleanMimeType = cleanMimeType.split(';')[0].trim();

        // Dateiendung bestimmen
        let fileExtension = 'webm';
        if (cleanMimeType.includes('mp4') || cleanMimeType.includes('m4a')) fileExtension = 'm4a';
        else if (cleanMimeType.includes('wav')) fileExtension = 'wav';
        else if (cleanMimeType.includes('mp3')) fileExtension = 'mp3';
        else if (cleanMimeType.includes('ogg')) fileExtension = 'ogg';

        // Blob erstellen
        const audioBlob = new Blob(audioChunksRef.current, { type: cleanMimeType });
        
        // --- FIX: LEERE DATEIEN ABFANGEN ---
        // 110 Bytes ist oft nur der leere WebM-Header. Wir prüfen auf Mindestgröße (z.B. 1KB).
        if (audioBlob.size < 1000) {
            console.warn("Aufnahme zu kurz/leer:", audioBlob.size, "Bytes");
            setError(t('recordingTooShort') || 'Recording too short or empty. Please speak longer.');
            
            // Stream aufräumen
            stream.getTracks().forEach(track => track.stop());
            return;
        }
        // -----------------------------------

        await processAudio(audioBlob, fileExtension, cleanMimeType);
        
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start(200);
      setIsRecording(true);
      
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      setError('Failed to access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    
    setIsRecording(false);
  };

  const processAudio = async (audioBlob, fileExtension, fileType) => {
    try {
      setIsProcessing(true);
      setError(null);
      
      // KEINE Konvertierung mehr - wir senden die Originaldatei
      // Das verhindert "Unrecognized file format" Fehler durch defekte Konvertierung
      
      const fileName = `voice-recording.${fileExtension}`;
      console.log(`Sending audio as ${fileName} (${fileType})`);

      const audioFile = new File([audioBlob], fileName, {
        type: fileType
      });

      //const result = await voiceSearch(audioFile, { limit: 20 });
      const result = await voiceSearch(audioFile, { 
          limit: 20,
          prompt: "Ein kurzer Sprachbefehl, eine Suchanfrage oder eine Frage. Beispiele: Dashboard, Quiz erstellen, Profil, Was steht im Dokument über Sicherheit?" 
      });

      if (result.status === 'success') {
        const spokenTextRaw = result.transcript || '';
        setTranscript(spokenTextRaw);
        // Sofortiges UI-Feedback: zeigt an, wohin das Backend geroutet hat
        setRoutingHint(result.is_rag ? 'rag' : 'search');

        // --- SPRACHBEFEHLE PRÜFEN ---
        const spokenTextLower = spokenTextRaw.toLowerCase().trim();
        const matchedCommand = VOICE_COMMANDS.find(cmd => 
          cmd.triggers.some(trigger => spokenTextLower.includes(trigger))
        );

        if (matchedCommand) {
          console.log(`[VoiceCommand] Triggered: "${spokenTextRaw}" -> Navigating to ${matchedCommand.route}`);
          setTimeout(() => {
              navigate(matchedCommand.route);
              onClose();
          }, 800);
          setIsProcessing(false);
          return; 
        }

        // --- RAG-Routing ---
        // Backend hat is_rag:true gesetzt aber die Antwort NICHT selbst generiert
        // (response_type = 'rag_pending') — wir rufen /document-chat direkt auf.
        // Das vermeidet den Timeout-Fehler im /voice-search Endpoint.
        if (result.is_rag) {
          console.log('[VoiceRAG] RAG-Routing erkannt, rufe /document-chat direkt auf...');

          try {
            // Bestehende Chat-History aus dem GlobalChatWidget lesen
            // (wird dort über window.__hejviChatHistory__ bereitgestellt).
            // Ermöglicht Folgefragen per Sprache mit vollem Kontext.
            const existingHistory = window.__hejviChatHistory__?.current ?? [];

            // AuthContext.makeAuthenticatedRequest gibt rohe Response zurück → .json() nötig
            const ragResponse = await makeAuthenticatedRequest('/document-chat', {
              method: 'POST',
              body: JSON.stringify({
                question:   spokenTextRaw,
                session_id: '',
                history:    existingHistory.slice(-12),
                language:   (navigator.language || 'de').split('-')[0],
              }),
            });
            const ragData = await ragResponse.json();

            console.log('[VoiceRAG] Antwort erhalten:', (ragData.answer || '').slice(0, 80));

            // Custom Event als Brücke zu Dashboard/GlobalChatWidget —
            // umgeht das fehlende Prop-Drilling durch BottomNavigation.
            window.dispatchEvent(new CustomEvent('hejvi:voice-rag-answer', {
              detail: {
                answer:     ragData.answer  || '',
                sources:    ragData.sources || [],
                transcript: spokenTextRaw,
              }
            }));

            // Fallback: falls onRagAnswer doch direkt übergeben wurde
            if (typeof onRagAnswer === 'function') {
              onRagAnswer({
                answer:     ragData.answer  || '',
                sources:    ragData.sources || [],
                transcript: spokenTextRaw,
              });
            }

            setTimeout(() => onClose(), 400);

          } catch (ragErr) {
            console.error('[VoiceRAG] /document-chat fehlgeschlagen:', ragErr);
            setError(t('generalError') || 'Antwort konnte nicht geladen werden.');
          }
          setIsProcessing(false);
          return;
        }

        // --- Normale Suche ---
        setTimeout(() => {
          onSearchResults(result);
          onClose(); 
        }, 500);
      } else {
        throw new Error(result.error || 'Voice search failed');
      }
      
    } catch (error) {
      
      console.error('Error processing audio:', error);
      
      // --- NEU: INTELLIGENTE FEHLERMELDUNG ---
      let rawMessage = (error.message || '').toString();
      let userMessage = t('generalError') || 'An unexpected error occurred.';

      // Prüfung auf spezifische Backend-Fehler
      if (rawMessage.includes('No speech detected') || rawMessage.includes('filtered noise')) {
          userMessage = t('errorNoSpeechDetected') || "Keine Sprache erkannt. Bitte sprechen Sie lauter.";
      } 
      else if (rawMessage.includes('Transcription failed') || rawMessage.includes('empty result')) {
          userMessage = t('errorTranscriptionFailed') || "Konnte Audio nicht verstehen. Bitte erneut versuchen.";
      }
      else if (rawMessage.includes('502')) {
          // 502 Bad Gateway deutet oft auf Absturz des Python-Scripts oder Timeouts hin
          userMessage = t('serverError') || "Serverfehler. Bitte versuchen Sie es später erneut.";
      }
      else {
          // Fallback: Wenn wir den Fehler nicht kennen, zeigen wir eine Standardmeldung 
          // statt dem hässlichen "HTTP 502..." String.
          userMessage = t('uploadFailed') || "Verarbeitung fehlgeschlagen.";
      }

      setError(userMessage);
      // ----------------------------------------


    } finally {
      setIsProcessing(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="voice-recorder-overlay">
      <div className="voice-recorder-modal">
        <div className="voice-recorder-header">
          <h2>{t('voiceSearch')}</h2>
          <button className="close-button" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="voice-recorder-content">
          {error && (
            <div className="error-message">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M15 9L9 15M9 9L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              {error}
            </div>
          )}

          

          <div className="recording-section">
            {!isRecording && !isProcessing && (
              <div className="recording-instructions">
                <div className="mic-icon">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                    <path d="M12 1A3 3 0 0 0 9 4V10A3 3 0 0 0 12 13A3 3 0 0 0 15 10V4A3 3 0 0 0 12 1Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M19 10V13A7 7 0 0 1 5 13V10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M12 19V23" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M8 23H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <p>{t('tapToStartRecording')}</p>
                <p className="recording-hint">{t('speakYourSearchQuery')}</p>
              </div>
            )}

            {/* Routing-Hinweis: erscheint sofort nach Transkription */}
            {transcript && !isProcessing && routingHint && (
              <div className={`voice-routing-hint voice-routing-hint--${routingHint}`}>
                {routingHint === 'rag' ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {t('routingHintRag') || 'Wird als Wissensfrage behandelt …'}
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                      <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    {t('routingHintSearch') || 'Wird als Suchanfrage behandelt …'}
                  </>
                )}
              </div>
            )}

            {isRecording && (
              <div className="recording-active">
                <div className="recording-indicator">
                  <div className="pulse-ring"></div>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                    <path d="M12 1A3 3 0 0 0 9 4V10A3 3 0 0 0 12 13A3 3 0 0 0 15 10V4A3 3 0 0 0 12 1Z" fill="#FF6407" stroke="#FF6407" strokeWidth="2"/>
                    <path d="M19 10V13A7 7 0 0 1 5 13V10" stroke="#FF6407" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M12 19V23" stroke="#FF6407" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M8 23H16" stroke="#FF6407" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <p className="recording-time">{formatTime(recordingTime)}</p>
                <p>{t('recording')}</p>
              </div>
            )}

            {isProcessing && (
              <div className="processing">
                <div className="spinner"></div>
                <p>{t('processingAudio')}</p>
              </div>
            )}
          </div>

          <div className="voice-recorder-actions">
            {!isRecording && !isProcessing && (
              <button 
                className="record-button"
                onClick={startRecording}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 1A3 3 0 0 0 9 4V10A3 3 0 0 0 12 13A3 3 0 0 0 15 10V4A3 3 0 0 0 12 1Z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M19 10V13A7 7 0 0 1 5 13V10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M12 19V23" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M8 23H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                {t('startRecording')}
              </button>
            )}

            {isRecording && (
              <button 
                className="stop-button"
                onClick={stopRecording}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect x="6" y="6" width="12" height="12" fill="currentColor"/>
                </svg>
                {t('stopRecording')}
              </button>
            )}

            {transcript && !isProcessing && (
              <button 
                className="search-again-button"
                onClick={() => {
                  setTranscript('');
                  setRoutingHint(null);
                  setRecordingTime(0);
                }}
              >
                {t('searchAgain')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default VoiceRecorder;